import { matchPath } from 'react-router-dom';
import { BOOK_ADHD } from '../../constants/routes';

declare global {
  interface Window {
    Intercom?: (...args: unknown[]) => void;
    intercomSettings?: Record<string, unknown>;
  }
}

const INTERCOM_API_BASE = 'https://api-iam.intercom.io';

const MAIN_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID ?? '';
const ADHD_APP_ID = process.env.REACT_APP_INTERCOM_ADHD_APP_ID ?? '';

let currentBootedAppId: string | null = null;
let loadingPromise: Promise<void> | null = null;
let loadedScriptAppId: string | null = null;

export function getIntercomAppIdForPathname(pathname: string): string {
  if (matchPath({ path: BOOK_ADHD, end: false }, pathname)) {
    return ADHD_APP_ID;
  }
  return MAIN_APP_ID;
}

function ensureIntercomStub(): void {
  if (typeof window.Intercom === 'function') return;

  const queue: unknown[][] = [];
  // eslint-disable-next-line operator-linebreak
  const stub: ((...args: unknown[]) => void) & { q: unknown[][]; c: (a: unknown[]) => void } =
    Object.assign(
      (...args: unknown[]) => { queue.push(args); },
      { q: queue, c: (a: unknown[]) => { queue.push(a); } },
    );
  window.Intercom = stub;
}

function removeIntercomScript(): void {
  const existing = document.getElementById('intercom-script');
  if (existing) {
    existing.remove();
  }
  loadedScriptAppId = null;
  loadingPromise = null;
}

function loadIntercomScript(appId: string): Promise<void> {
  if (loadedScriptAppId === appId && loadingPromise) {
    return loadingPromise;
  }

  removeIntercomScript();

  window.intercomSettings = {
    app_id: appId,
    api_base: INTERCOM_API_BASE,
  };

  ensureIntercomStub();

  loadingPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.id = 'intercom-script';
    s.type = 'text/javascript';
    s.async = true;
    s.src = `https://widget.intercom.io/widget/${appId}`;
    s.onload = () => {
      loadedScriptAppId = appId;
      resolve();
    };
    s.onerror = () => reject(new Error(`Failed to load Intercom widget for ${appId}`));
    const x = document.getElementsByTagName('script')[0];
    x.parentNode?.insertBefore(s, x);
  });

  return loadingPromise;
}

export async function bootIntercom(
  appId: string,
  signal?: AbortSignal,
): Promise<void> {
  if (!appId) return;

  await loadIntercomScript(appId);

  if (signal?.aborted) return;

  window.Intercom?.('boot', {
    app_id: appId,
    api_base: INTERCOM_API_BASE,
  });
  currentBootedAppId = appId;
}

export function shutdownIntercom(): void {
  window.Intercom?.('shutdown');
  currentBootedAppId = null;
}

export function updateIntercom(): void {
  window.Intercom?.('update');
}

export function getCurrentBootedAppId(): string | null {
  return currentBootedAppId;
}

export async function switchIntercomWorkspace(
  newAppId: string,
  signal?: AbortSignal,
): Promise<void> {
  if (!newAppId) return;

  if (currentBootedAppId === newAppId) {
    updateIntercom();
    return;
  }

  if (currentBootedAppId) {
    shutdownIntercom();
  }
  removeIntercomScript();
  await bootIntercom(newAppId, signal);
}
